import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  GroupedSelect,
  Picker,
  Tooltip,
  Layout,
  Checkbox,
  EmptyState
} from '@iba-team/portal-react-components/dist';
import LeaveTypes, { requireIcon } from '../LeaveTypes';
import { ReactComponent as EmptyList } from '../../assets/icons/no-requests.svg';
import {
  eachDayOfInterval, endOfMonth,
  format, getISOWeek, getWeek,
  isEqual,
  startOfMonth,
  getYear, getMonth, getDate
} from 'date-fns';
import {
  fetchTooltipDesc,
  handleTdClassName,
  monthToString,
  weekdayStylesExpression
} from '../planner/Planner';
import './ReportProject.scss';
import { LeaveTypesStore } from '../../store/LeaveTypesList';
import { useTranslation } from 'react-i18next';
import { useEffectOnce, useSessionStorage, useSetState, useUpdateEffect } from 'react-use';
import { ReportPlannerStore } from '../../store/ReportPlanner';
import { observer } from 'mobx-react';
import { BackArrow } from './BackArrow';
import moment from 'moment';
import { groupBy } from 'fp-ts/NonEmptyArray';
import { TypeLegend } from '../../utils_components/TypeLegend';
import { TotalAbsenceCategory } from '../../types';
import { useUsageStatistics } from '../../utils/hooks';
import apiConfig from '../../api/routes';
import ExportButton from '../../utils_components/ExportButton';
import { buildDownloadFile, isFirefox } from '../../utils/functions';
import { ArrowButtons } from '../../utils_components/ArrowButtons';
import UserLink from '../../utils_components/UserLink';

export type ListType  = 'PROJECT' | 'PLANNER_LIST' | 'CIRCLE'

type ReducedListType = {
  [K in ListType]: Project[]
}

export type Project = { projectId: number, name: string, type: 'PROJECT', } | { circleTeamId: number, name: string, type: 'CIRCLE' } | { plannerListId: number, name: string, type: 'PLANNER_LIST'
}


const REPORT_RELEASE_DATE = new Date('2022-03-01');

type ProjectLeavesI = {
  obligatory: string[],
  optional: string[],
}

export const ReportProject = observer(() => {
  const { getAllLeaveTypes, allLeaveTypes } = LeaveTypesStore;
  const {
    getProjectData, getProjectList, projectList,
    projectData, isDataFetched, downloadProjectReport, isReportDownloading
  } = ReportPlannerStore;
  const { t } = useTranslation();
  const [params, setParams] = useSetState<Project>( {projectId: 0, name: '', type: 'PROJECT'});
  const [dateList, setDateList] = useState<Date[]>([]);
  const [showPendingRequests, setShowPendingRequests] = useState(false);
  const [dateSession, setDateSession] = useSessionStorage('PROJECT_REPORT', {
    currentDate: new Date(),
  });

  useUsageStatistics(apiConfig.statisticsUsageService.projectReportPage);

  useEffect(() => {
    setShowPendingRequests(projectList.selectedFilters.showPendingRequests);
  },[projectList.selectedFilters.showPendingRequests]);

  useEffect(()=>{
    getAllLeaveTypes();
  },[]);

  useEffect(()=> {
    setDateList(eachDayOfInterval({
      start: startOfMonth(new Date(dateSession.currentDate)),
      end: endOfMonth(new Date(new Date(dateSession.currentDate)))
    }));
  },[dateSession.currentDate]);

  const groupByWeek = (list: Date[]) => {
    return groupBy((date: Date) => getYear(date) + '-' + getWeek(date, {
      weekStartsOn: 1
    }) as unknown as string)(list);
  };

  const grouped = useMemo(() => {
    return Object.values(groupByWeek(dateList));
  }, [dateList]);

  const unPackageGroupedList = useMemo(()=> {
    return grouped.flat();
  },[grouped]);

  useEffectOnce(()=>{
    getProjectList();
  });

  useUpdateEffect(()=>{
    if(projectList.selectedItem.type === 'CIRCLE'){
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setParams({name: projectList.selectedItem.name, circleTeamId: projectList.selectedItem.circleTeamId, type: projectList.selectedItem.type});
    }
    if(projectList.selectedItem.type === 'PROJECT'){
      setParams({name: projectList.selectedItem.name, projectId: projectList.selectedItem.projectId, type: projectList.selectedItem.type});
    }
    if(projectList.selectedItem.type === 'PLANNER_LIST'){
      setParams( {name: projectList.selectedItem.name, plannerListId: projectList.selectedItem.plannerListId, type: projectList.selectedItem.type});
    }
  },[projectList]);


  useEffect(()=> {
    if(params.name.length){
      if(params.type === 'PLANNER_LIST'){
        // getProjectData(params.project.plannerListId, format(new Date(CALENDAR.value),'yyyy-MM'), true);
        getProjectData(params.plannerListId, format(new Date(dateSession.currentDate),'yyyy-MM'), 'PLANNER_LIST', showPendingRequests).then(() => {
          projectList.selectedFilters.showPendingRequests = showPendingRequests;
          projectList.selectedItem = params;
        });
      }
      if(params.type === 'PROJECT'){
        // getProjectData(params.project.plannerListId, format(new Date(CALENDAR.value),'yyyy-MM'), true);
        getProjectData(params.projectId, format(new Date(dateSession.currentDate),'yyyy-MM'), 'PROJECT', showPendingRequests).then(() => {
          projectList.selectedFilters.showPendingRequests = showPendingRequests;
          projectList.selectedItem = params;
        });
      }
      if(params.type === 'CIRCLE'){
        // getProjectData(params.project.plannerListId, format(new Date(CALENDAR.value),'yyyy-MM'), true);
        getProjectData(params.circleTeamId, format(new Date(dateSession.currentDate),'yyyy-MM'), 'CIRCLE', showPendingRequests).then(() => {
          projectList.selectedFilters.showPendingRequests = showPendingRequests;
          projectList.selectedItem = params;
        });
      }
    }
  },[params, dateSession.currentDate, showPendingRequests]);

  const datesMapping = (list: Date[], variant: string) => {
    return list.map(cell=> <td  key={cell.toString()} className={weekdayStylesExpression(cell, true)}>{format(cell,variant)}</td>);
  };

  const stringDays = useMemo(()=>{
    return datesMapping(unPackageGroupedList, 'EEEEEE');
  },[unPackageGroupedList]);

  const numberDays = useMemo(()=> {
    return datesMapping(unPackageGroupedList, 'd');
  },[unPackageGroupedList]);


  const tableRef = useRef<HTMLTableElement>(null);
  const employeeRef = useRef<HTMLTableElement>(null);
  const onScrollTable = (e:any ) => {

    if(tableRef?.current && employeeRef.current){
      employeeRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };

  const onScrollEmployee = (e:any) =>{
    if(tableRef?.current && employeeRef.current){
      tableRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };

  useEffect(() => {
    if(tableRef?.current && employeeRef.current){
      tableRef?.current.addEventListener('scroll', onScrollTable);
    } else {
      window.removeEventListener('scroll', onScrollTable);
    }

    if(tableRef?.current && employeeRef.current){
      employeeRef?.current.addEventListener('scroll', onScrollEmployee);
    } else {
      window.removeEventListener('scroll', onScrollEmployee);
    }

  }, [isDataFetched, employeeRef.current, tableRef.current]);

  const workingDaysForCountry = projectData.workingDaysForProject.map((item,index)=>
    <div className={'countryCode'}>
      <span>
        {item.countryCode} -
      </span>
      <span>
        {item.totalDays}
      </span>
      {index !== projectData.workingDaysForProject.length-1 && ','}
    </div>
  );

  const disabledDate = (value: moment.Moment)=>{
    return value && value.toDate() < REPORT_RELEASE_DATE;
  };

  const prepareList = projectList.list.reduce((prev, val) => {
    const current = prev[val.type];
    const check = Array.isArray(current);
    return {...prev,[val.type]: check ? [...current, {...val}] : [{...val}]};
  }, {} as unknown as ReducedListType);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const sortedProjectList: [string, Project[]][] = [[t('CIRCLE'), prepareList.CIRCLE],[t('PROJECT'), prepareList.PROJECT],[t('PLANNER_LIST'), prepareList.PLANNER_LIST]].filter(i => i[1]);

  const getOnlyActiveCategories = () : ProjectLeavesI => {
    const isLeaveCategoryRequired = (category: TotalAbsenceCategory ) => category.leaveCategory === 'VACATION' || category.leaveCategory === 'UNPAID_LEAVE' || category.leaveCategory === 'SICK_LEAVE';
    const categoriesList = projectData.dataGrid.map(i => i.employeeDto.totalAbsence);
    const obligatoryTemplates: Record<string, number> = {};
    const optionalTemplates: Record<string, number> = {};
    for(let i = 0; i < categoriesList.length; i++){
      const userCategories = categoriesList[i];
      for(let j = 0; j < userCategories.length; j ++){
        const category = userCategories[j];
        if (isLeaveCategoryRequired(category)) {
          obligatoryTemplates[category.leaveCategory] = category.leaveCount;
        }
        else if(category.leaveCount >= 1) {
          optionalTemplates[category.leaveCategory] = category.leaveCount;
        }
      }
    }

    return {
      obligatory: Object.keys(obligatoryTemplates).sort((a, b) => a.length - b.length),
      optional: Object.keys(optionalTemplates),
    };
  };

  const activeCategories = useMemo(()=> {
    return getOnlyActiveCategories();
  },[projectData]);

  const buildFile = () => {
    const dateForQuery = format(new Date(dateSession.currentDate), 'yyyy-MM');
    const dateForFileName = format(new Date(dateSession.currentDate), 'yyyy_MM');
    let projectId = 0;

    switch (params.type) {
    case 'PROJECT':
      projectId = params.projectId;
      break;
    case 'CIRCLE':
      projectId = params.circleTeamId;
      break;
    case 'PLANNER_LIST':
    default:
      projectId = params.plannerListId;
    }

    downloadProjectReport(showPendingRequests, params.type, projectId, dateForQuery).then((response) => {
      return response?.blob();
    }).then((blob) => {
      buildDownloadFile(blob, `${dateForFileName}_${params.name}.xlsx`);
    });
  };

  const getValidDate = () => {
    if (dateSession.currentDate !== null) {
      return new Date(dateSession.currentDate);
    }
    return new Date();
  };

  const prevMonth = () => {
    const currDate = getValidDate();
    setDateSession({currentDate: new Date(getYear(currDate), getMonth(currDate) - 1, getDate(currDate))});
  };

  const nextMonth = () => {
    const currDate = getValidDate();
    setDateSession({currentDate: new Date(getYear(currDate), getMonth(currDate) + 1, getDate(currDate))});
  };

  const renderHeader = () => {
    return {
      layoutName:  <BackArrow text={'projectReportTitle'} reportType={'Project'}/>,
      right:
        <div className={'selectBlock'}>
          <div>
            <Checkbox checked={showPendingRequests} label={'Show pending requests'} setChecked={setShowPendingRequests}/>
          </div>
          <ArrowButtons onLeftClick={prevMonth} onRightClick={nextMonth}/>
          {
            <Picker
              className={'projectDatePicker'}
              disabledDate={disabledDate}
              picker={'month'}
              onChange={e => setDateSession({currentDate: e? e._d : new Date()})}
              format={'MMM, yyyy'} value={dateSession.currentDate?.toString()}
            />
          }
          <GroupedSelect<Project>
            disabled={projectList.list.length <=1 || undefined}
            className={'projectSelect'}
            list={sortedProjectList}
            value={params.name.length ? params.name :  projectList.selectedItem.name}
            onChangeValue={(value) => {
              setParams(value);
            }}
            render={(item) => <div style={{width: '100%', overflow: 'hidden'}} title={item.name}>{item.name}</div>}
            uniqueElement={el => {
              if(params.type === 'PROJECT' && el.type === 'PROJECT'){
                return el.projectId === params.projectId;
              }
              if(params.type === 'PLANNER_LIST' && el.type === 'PLANNER_LIST'){
                return el.plannerListId === params.plannerListId;
              }
              if(params.type === 'CIRCLE' && el.type === 'CIRCLE'){
                return el.circleTeamId === params.circleTeamId;
              }
              return false;
            }}
          />
          <ExportButton isDownloading={isReportDownloading} onClick={buildFile} />
        </div>
    };
  };

  const renderHeaderCategories = (categories: string[]) =>
    categories.map((category) => projectData.dataGrid[0]?.employeeDto?.totalAbsence
      .filter(i => category === i.leaveCategory)
      .map((i) => <td className={'planner_table__stats-header-type-categories'}>
        <span>
          <img src={ requireIcon(i.leaveCategory) } alt={'leaveType'}/>
        </span>
      </td>));

  const renderBodyCategories = (employeeTotalAbsence: TotalAbsenceCategory[], categories: string[]) =>
    categories.map((category) => employeeTotalAbsence
      .filter(i => i.leaveCategory === category)
      .map(i => <td><div>{i.leaveCount}</div></td>));

  const tableWidthStyle = {
    minWidth: `${500 + activeCategories.optional.length * 43}px`,
  };

  const scrollbarClassName = isFirefox() ? 'reportHeight__scrollbar_firefox' : 'reportHeight__scrollbar';

  return (
    <Layout isLoading={!isDataFetched} className={'planner planer-projects'} header={renderHeader()}>
      {projectData.dataGrid.length ?
        <>
          <div className={'report_header'}>
            <div className={'description'}>
              <div className={'countryWorkingDays'}><div>
                {t('workingDays')}:</div>
              {workingDaysForCountry.map(item=>item)}
              </div>
              <TypeLegend placement={'bottomLeft'} list={allLeaveTypes} renderItem={i => i.requestName}/>
              {/*{RADIO && workingDaysForCountry.length > 0 &&*/}
              {/*  <div className={'countryWorkingDays'}><div>{t(RADIO.value === 'DAYS' ? 'workingDays': 'workingHours')}:</div> <div>{workingDaysForCountry.map(item=>item)}</div></div>*/}
              {/*}*/}
            </div>
          </div>
          <div className={'planner_table__wrapper planner_report_wrapper'} >
            <table ref={employeeRef}
              className={`planner_table__wrapper-search-block reportHeight ${scrollbarClassName}`} style={tableWidthStyle}>
              <thead>
                <tr style={{display: 'flex'}}>
                  <td className={'user_index'}>#</td>
                  <td className={'user_description'}>
                    {t('employee')}
                  </td>
                  <td rowSpan={3} className={'planner_table__stats-header-type-stats title-total'}>
                    <Tooltip title={<div>{t('WorkedTooltipDays')}</div>}>
                      <div className={'planner_table__stats-header-type-title'}>Worked</div>
                    </Tooltip>
                  </td>
                  <td rowSpan={3} className={'planner_table__stats-header-type-stats title-used'}>
                    <Tooltip title={<div>{t('UsedTooltipDays')}</div>}>
                      <div className={'planner_table__stats-header-type-title'}>Used</div>
                    </Tooltip>
                  </td>
                  <div className={'stats_categories_wrapper'}>
                    <td colSpan={4} rowSpan={2} className={'planner_table__stats-header-type-title'}>Category</td>
                    {renderHeaderCategories(activeCategories.obligatory)}
                    {renderHeaderCategories(activeCategories.optional)}
                  </div>
                </tr>
              </thead>
              <tbody>
                {projectData.dataGrid.map(({userActive, profileLinkVisible, employeeDto, orderNumber}) =>
                  <tr style={{display: 'flex'}} className="user_report_row">
                    <td className={'user_index'}>
                      <div>{orderNumber}</div>
                    </td>
                    <td className="user_report_column" >
                      <UserLink 
                        id={employeeDto.id}
                        fullName={employeeDto.fullName}
                        countryCode={employeeDto.countryCode}
                        userActive={userActive}
                        profileLinkVisible={profileLinkVisible}
                        openInNewTab={true}
                      />
                    </td>
                    <td className={'planner_table__stats-body stats_worked'}>
                      <div className={'planner_table__stats-body-title'}>
                        <div>{employeeDto.totalWorkedForParticipant}</div>
                      </div>
                    </td>
                    <td className={'planner_table__stats-body stats_used'}>
                      <div className={'planner_table__stats-body-title title-used-outer'}>
                        <div>{employeeDto.totalUsedForParticipant}</div>
                      </div>
                    </td>
                    {renderBodyCategories(employeeDto.totalAbsence, activeCategories.obligatory)}
                    {renderBodyCategories(employeeDto.totalAbsence, activeCategories.optional)}
                  </tr>

                )}
              </tbody>
            </table>
            <table style={{maxHeight: 'auto !important'}} className={'planner_table report_table'} ref={tableRef}>
              <thead className='planner_table__thead-stiky'>
                <tr className={'planner_report_weeks_line'}>
                  {grouped.map((dateSet, idx) => {
                    const weekIndex = getISOWeek(dateSet[0]);
                    const firstM = monthToString(dateSet[0]);
                    return dateSet.length <= 4 ? <Tooltip title={`Week ${weekIndex}: ${firstM} `}>
                      <td key={idx} colSpan={dateSet.length} style={{cursor: 'pointer'}} className={'bgCustom'}>Week {weekIndex}: {firstM} </td>
                    </Tooltip> : <td key={idx} colSpan={dateSet.length} className={'bgCustom'}>Week {weekIndex}: {firstM} </td>;
                  })
                  }
                </tr>
                <tr className={'planner_days'}>
                  {stringDays}
                </tr>
                <tr className={'planner_dates reportDates'}>
                  {numberDays}
                </tr>
              </thead>
              <tbody style={{display: 'block'}}>
                <>
                  { projectData.dataGrid.map(({employeeDto})=>
                  {
                    return <tr key={employeeDto.id} className={'user_events_list report_project_events_list'}>
                      {
                        unPackageGroupedList.map((elem,index)=>{
                          const findArrOfElements = employeeDto.events.map(i => ({ dates: eachDayOfInterval({start: new Date(i.startDate), end: new Date(i.endDate)}), event: i}));
                          const findElement = findArrOfElements.find(i => i.dates.find(event => isEqual(event, elem)));
                          return <Tooltip title={fetchTooltipDesc(findElement?.event, t)}>
                            <td key={index}
                              className={handleTdClassName(findElement?.event, elem, false)}
                            >
                              {findElement?.event.type && findElement?.event.countryCode === null && findElement.event.originalTypeName?
                                <LeaveTypes
                                  leaveTypes={allLeaveTypes}
                                  withoutText={true}
                                  type={findElement?.event.originalTypeName}
                                  infoMessageVisible={false}/>
                                : findElement?.event.countryCode !== null && findElement?.event.type ?
                                  <div className={'planner_table_cell__country'}>{findElement?.event.countryCode}</div> : ''
                              }
                            </td>
                          </Tooltip>;
                        })
                      }
                    </tr>;
                  })
                  }
                </>
              </tbody>
            </table>
          </div>
        </>
        :
        <div className='employee-block-default default_block_report'>
          <EmptyState title={t('emptyListTitle')} description={t('emptyListDescr')} icon={<EmptyList/>}/>
        </div>
      }
    </Layout>
  );
});
